import React, { useMemo } from "react";
import ProgressTracker from '../../../../utils/ProgressTracker'
import { useCurrencySymbol } from '../../../../utils/currencyUtils';
import DateConversion from '../../../../utils/DateConversion';
import CommaConvert from '../../../../utils/CommaConvert';
import {
  formatAmountComma,
  parseAmountNumber,
} from '../../../../utils/decimalAmountUtils';
import { useSelector } from "react-redux";
import {
  consolidateTrainingExpenseRowsForDisplay,
  isTrainingExpenseDisplayRow,
} from "../../../../utils/trainingOtherExpenseSync";

const ShowCompletedTasksOP = ({
  projectTrackingWorkEntries,
  matchedRowDescription,
  taskKey,
  expenseKey,
  matchedHardwareParts,
  row,
  loggedEmp,
  showListState,
  isViewOnly = false }) => {

  const currency = useCurrencySymbol();
  const { formatDateByCountry } = DateConversion();
  const USAComma = CommaConvert();
  const { mytrainings } = useSelector((state) => state.myTrainings);
  const formatHours = (value) =>
    new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    }).format(Number(value) || 0);

  const lockedTrainingRow = useMemo(() => {
    const trainingsList = Array.isArray(mytrainings?.trainings)
      ? mytrainings.trainings
      : Array.isArray(mytrainings)
        ? mytrainings
        : [];

    const normKey = (v) => String(v || "").trim().toLowerCase();
    const resolveRefId = (ref) => {
      if (ref == null || ref === "") return "";
      if (typeof ref === "object")
        return String(ref?._id ?? ref?.id ?? "").trim();
      return String(ref).trim();
    };
    const pick = (obj, keys) => {
      for (const k of keys) {
        const val = obj?.[k];
        if (val != null && String(val).trim() !== "") return val;
      }
      return "";
    };

    if (!row || trainingsList.length === 0) return null;
    const loggedEmail = String(localStorage.getItem("email") || "")
      .trim()
      .toLowerCase();
    const assigneeId = resolveRefId(loggedEmp?._id);
    if (!loggedEmail && !assigneeId) return null;

    const leadLike = row?.lead || row?.Lead || null;
    const projectIdCandidates = new Set(
      [
        resolveRefId(
          pick(row, ["_id", "id", "ProjectID", "projectid", "projectId", "ProjectId"])
        ),
        resolveRefId(pick(leadLike, ["_id", "id"])),
      ]
        .map((x) => normKey(x))
        .filter(Boolean)
    );
    const quotationIdCandidates = new Set(
      [
        pick(row, ["quotationId", "quotationID", "quotationid", "QuotationId", "QuotationID"]),
        pick(leadLike, ["quotationId", "quotationID", "quotationid", "QuotationId", "QuotationID"]),
      ]
        .map((x) => String(x || "").trim())
        .filter(Boolean)
    );
    const projectNameCandidates = new Set(
      [pick(row, ["ProjectName", "projectname"]), pick(leadLike, ["ProjectName", "projectname"])]
        .map((x) => normKey(x))
        .filter(Boolean)
    );
    const leadIdCandidates = new Set(
      [pick(row, ["leadid", "leadId", "LeadId", "LeadID"]), pick(leadLike, ["leadid", "leadId", "LeadId", "LeadID"])]
        .map((x) => String(x || "").trim())
        .filter(Boolean)
    );

    const matched = trainingsList.filter((t) => {
      const assignedToRaw = t?.assignedto ?? t?.assignedTo;
      const assignedToId = resolveRefId(assignedToRaw);
      const assignedToEmail =
        typeof assignedToRaw === "string"
          ? normKey(assignedToRaw)
          : normKey(t?.assignedToEmail ?? "");
      const matchesAssignee =
        (assigneeId && assignedToId && String(assignedToId) === String(assigneeId)) ||
        (loggedEmail && assignedToEmail && assignedToEmail === loggedEmail);
      if (!matchesAssignee) return false;

      const tProjectId = normKey(resolveRefId(t?.projectid ?? t?.projectId ?? t?.project));
      const tQuotationId = String(t?.quotationId ?? t?.quotationID ?? t?.quotationid ?? "").trim();
      const tProjectName = normKey(t?.projectname ?? t?.ProjectName);
      const tLeadId = String(t?.leadid ?? t?.leadId ?? t?.LeadId ?? "").trim();

      return (
        (tProjectId && projectIdCandidates.has(tProjectId)) ||
        (tQuotationId && quotationIdCandidates.has(tQuotationId)) ||
        (tProjectName && projectNameCandidates.has(tProjectName)) ||
        (tLeadId && leadIdCandidates.has(tLeadId))
      );
    });

    const normalized = matched.map((t) => {
      const amount = Number(
        String(t?.trainingcost ?? t?.trainingCost ?? 0).replace(/,/g, "")
      );
      const notes = t?.notes ?? t?.note ?? "";
      return {
        amount: Number.isFinite(amount) ? amount : 0,
        notes: String(notes || "").trim(),
      };
    });

    const total = normalized.reduce((sum, x) => sum + (Number(x.amount) || 0), 0);
    const joinedNotes = Array.from(
      new Set(normalized.map((x) => x.notes).filter(Boolean))
    ).join("; ");
    if (total <= 0 && !joinedNotes) return null;

    return {
      _id: `training_cost_${assigneeId || loggedEmail}`,
      description: "Training Cost",
      value: total,
      startdate: row?.DateRequested ?? null,
      enddate: null,
      notes: joinedNotes,
      isTaskComplete: true,
      expenses: total,
      _isTrainingCost: true,
    };
  }, [mytrainings, row, loggedEmp?._id]);

  const displayOtherExpenses = useMemo(() => {
    const base = Array.isArray(matchedRowDescription) ? matchedRowDescription : [];
    const hasTrainingAlready = base.some(
      (d) => isTrainingExpenseDisplayRow(d)
    );
    if (!lockedTrainingRow || hasTrainingAlready) {
      return consolidateTrainingExpenseRowsForDisplay(base);
    }
    return consolidateTrainingExpenseRowsForDisplay([...base, lockedTrainingRow]);
  }, [matchedRowDescription, lockedTrainingRow]);

  return (
    <div className=' pb-3'>
      {row?.TypeService?.toLowerCase().includes("hardware")
        // {row.TypeService === "Hardware"
        ? showListState === "tasktable" && (
          <div className="w-full max-h-[24rem] overflow-y-auto overflow-x-hidden">
            {/* Header */}
            <div className="sticky top-0 z-[2399] flex text-center bg-[#e8e8e8] text-black text-[10px] font-semibold break-all whitespace-normal">
              <div className="p-1 w-[14%] text-center">
                Part Name
              </div>
              <div className="p-1 w-[10%] text-center">
                Quantity
              </div>
              <div className="p-1 w-[10%]">Total Price ({currency})</div>
              <div className="p-1 w-[11%] text-center">
                Start Date
              </div>
              <div className="p-1 w-[11%]">End Date</div>
              <div className="p-1 w-[44%] text-center">
                Tracker
              </div>
            </div>

            <div
              className="flex text-center text-[10px] break-all whitespace-normal max-h-14"
            >
              <div className="p-1 border-b border-[#dee2e6] w-[14%]">
                {"Total"}
              </div>

              <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                {USAComma(matchedHardwareParts?.reduce((sum, item) => sum + (item?.quantity || 0), 0))}                  </div>
              <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                {USAComma(matchedHardwareParts?.reduce((sum, item) => sum + (item?.totalprice || 0), 0))}
              </div>
              <div className="p-1 border-b border-[#dee2e6] w-[11%] text-center">
              </div>
              <div className="p-1 border-b border-[#dee2e6] w-[11%] text-center">
              </div>
              <div className="p-2.5 border-b border-[#dee2e6] w-[44%] text-center">
                {matchedHardwareParts?.every((entry) => entry?.isTaskComplete) ? (
                  <>
                    <ProgressTracker value={100} />
                  </>
                ) : (
                  <>
                    {(() => {
                      const allEntries = matchedHardwareParts?.length;
                      const completedEntriesCount = matchedHardwareParts?.filter(entry => entry.isTaskComplete).length;
                      const trackerPercentage = allEntries > 0 ? (completedEntriesCount / allEntries) * 100 : 0;
                      return <ProgressTracker value={trackerPercentage} />;
                    })()}
                  </>
                )}
              </div>
            </div>

            {/* Rows */}
            {matchedHardwareParts?.length > 0 ? (
              matchedHardwareParts?.map((entry, index) => (
                <div
                  key={entry}
                  className="flex text-center text-[10px] break-all whitespace-normal max-h-14 cursor-default"
                >
                  <div className="p-1 border-b border-[#dee2e6] w-[14%] max-h-14 overflow-auto"
                    title={`${entry.assemblepartdescription}`}
                  >
                    {entry.assemblepartdescription}
                  </div>
                  <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                    {entry.quantity || ""}
                  </div>
                  <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                    {USAComma(entry.totalprice) || ""}
                  </div>
                  <div className="p-1 border-b border-[#dee2e6] w-[11%] text-center">
                    {entry.startdate
                      ? formatDateByCountry(entry.startdate)
                      : ""}
                  </div>
                  <div className="p-1 border-b border-[#dee2e6] w-[11%] text-center">
                    {entry.enddate
                      ? formatDateByCountry(entry.enddate)
                      : ""}
                  </div>
                  <div className="p-2.5 border-b border-[#dee2e6] w-[44%] text-center">
                    <ProgressTracker value={entry.isTaskComplete ? 100 : 0} />
                  </div>
                </div>
              ))
            ) : (
              <div className="flex text-[14px] break-all whitespace-normal max-h-14 p-1">
                <p className="text-red-500 text-center">
                  {" "}
                  No Tracking Task Entries Found
                </p>
              </div>
            )}
          </div>

        ) : showListState === "tasktable" && (
          <>
            <div className="w-full max-h-[24rem] overflow-y-auto overflow-x-hidden">
              {/* Header */}
              <div className="sticky top-0 z-[2399] flex text-center bg-[#e8e8e8] text-black text-[10px] font-semibold break-all whitespace-normal">
                <div className="p-1 w-[10%] text-center">
                  Tasks
                </div>
                <div className="p-1 w-[10%] text-center">
                  Hours <br /> Assigned
                </div>{" "}
                <div className="p-1 w-[10%] text-center">
                  Hours Spent
                </div>{" "}
                <div className="p-1 w-[10%] text-center">
                  Start Date
                </div>
                <div className="p-1 w-[10%]">End Date</div>
                <div className="p-1 w-[10%]">Notes</div>
                <div className="p-1 w-[40%] text-center">
                  Tracker
                </div>
              </div>
              <div
                className="flex text-center text-[10px] break-all whitespace-normal max-h-14"
              >
                <div className="p-1 border-b border-[#dee2e6] w-[10%]">
                  {"Total"}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%]">
                  {formatHours(
                    projectTrackingWorkEntries?.reduce(
                      (sum, item) => sum + (item?.hoursassigned || 0),
                      0
                    )
                  )}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                  {formatHours(
                    projectTrackingWorkEntries?.reduce(
                      (sum, item) => sum + (item?.hoursspent || 0),
                      0
                    )
                  )}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                </div>
                <div className="p-2.5 border-b border-[#dee2e6] w-[40%] text-center">
                  {projectTrackingWorkEntries?.every((entry) => entry?.isTaskComplete) ? (
                    <>
                      <ProgressTracker value={100} />
                    </>
                  ) : (
                    <>
                      {(() => {
                        const hoursAssigned = projectTrackingWorkEntries?.reduce((sum, item) => sum + (item?.hoursassigned || 0), 0);
                        const hoursSpent = projectTrackingWorkEntries?.reduce((sum, item) => sum + (item?.hoursspent || 0), 0);
                        const trackerPercentage = hoursAssigned > 0 ? (hoursSpent / hoursAssigned) * 100 : 0;
                        return <ProgressTracker value={trackerPercentage} />;
                      })()}
                    </>
                  )}
                </div>
              </div>

              {/* Rows */}
              {projectTrackingWorkEntries.length > 0 ? (
                projectTrackingWorkEntries.map((entry, index) => (
                  <div
                    key={entry}
                    className="flex  text-[10px] text-center break-all whitespace-normal max-h-14 cursor-default"
                  >
                    <div className="p-1 border-b border-[#dee2e6] w-[10%] overflow-auto max-h-14"
                      title={`${entry.task}`}
                    >
                      {entry.task || ""}
                    </div>
                    <div className="p-1 border-b border-[#dee2e6] w-[10%]">
                      {entry.hoursassigned !== undefined &&
                      entry.hoursassigned !== null &&
                      entry.hoursassigned !== ""
                        ? formatHours(entry.hoursassigned)
                        : ""}
                    </div>
                    <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                      {entry.hoursspent
                        ? formatHours(entry.hoursspent)
                        : ""}
                    </div>
                    <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                      {entry.startdate
                        ? formatDateByCountry(entry.startdate)
                        : ""}                       </div>
                    <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                      {entry.enddate
                        ? formatDateByCountry(entry.enddate)
                        : ""}                       </div>
                    <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center max-h-14 overflow-auto"
                      title={`${entry.notes}`}
                    >
                      {entry.notes}
                    </div>
                    <div className="p-2.5 border-b border-[#dee2e6] w-[40%] text-center">
                      {entry?.isTaskComplete ? (
                        <ProgressTracker
                          value={100}
                        />
                      ) : (
                        <ProgressTracker
                          value={Math.max(
                            0,
                            Number(entry?.hoursassigned) > 0
                              ? ((Number(entry?.hoursspent) || 0) /
                                Number(entry?.hoursassigned)) *
                              100
                              : 0
                          )}
                        />
                      )}

                    </div>
                  </div>
                ))
              ) : (
                <div className="flex text-[14px] break-all whitespace-normal max-h-14 p-1">
                  <p className="text-red-500 text-center">
                    {" "}
                    No Tracking Task Entries Found
                  </p>
                </div>
              )}
            </div>
          </>
        )}


      {showListState === "otherexpenses" && (
        <div className="w-full max-h-[24rem] overflow-y-auto overflow-x-hidden">
          <div className="sticky top-0 z-[2399] flex bg-[#e8e8e8] text-black text-[10px] font-semibold break-all whitespace-normal">
            <div className="p-1 w-[10%] text-center">
              Description
            </div>
            <div className="p-1 w-[10%] text-center">
              Amount ({currency})
            </div>
            <div className="p-1 w-[10%] text-center">
              Start Date
            </div>
            <div className="p-1 w-[10%]">End Date</div>
            <div className="p-1 w-[10%] text-center">
              Notes
            </div>
            <div className="p-1 w-[10%] text-center">
              Expenses ({currency})
            </div>
            <div className="p-1 w-[40%] text-center">
              Tracker
            </div>
          </div>

          <div
            className="flex text-center text-[10px] break-all whitespace-normal max-h-14"
          >
            <div className="p-1 border-b border-[#dee2e6] w-[10%]">
              {"Total"}
            </div>
            <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
              {formatAmountComma(displayOtherExpenses?.reduce((sum, item) => sum + parseAmountNumber(item?.value), 0))}
            </div>
            <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
            </div>
            <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
            </div>
            <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
            </div>
            <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
              {formatAmountComma(displayOtherExpenses.reduce((sum, item) => sum + parseAmountNumber(item?.expenses), 0))}
            </div>
            <div className="p-2.5 border-b border-[#dee2e6] w-[40%] text-center">
              {displayOtherExpenses?.every((entry) => entry?.isTaskComplete) ? (
                <>
                  <ProgressTracker value={100} />
                </>
              ) : (
                <>
                  {(() => {
                    const allEntries = displayOtherExpenses?.reduce((sum, item) => sum + (item?.value || 0), 0);
                    const completedEntriesCount = displayOtherExpenses?.reduce((sum, item) => sum + (item?.expenses || 0), 0);
                    const trackerPercentage = allEntries > 0 ? (completedEntriesCount / allEntries) * 100 : 0;
                    return <ProgressTracker value={trackerPercentage} />;
                  })()}
                </>
              )}
            </div>
          </div>

          {displayOtherExpenses?.length > 0 ? (
            displayOtherExpenses?.map((entry, index) => (
              <div
                key={entry}
                className="flex text-[10px] break-all whitespace-normal max-h-14 text-center cursor-default"
              >
                <div className="p-1 border-b border-[#dee2e6] w-[10%] max-h-14 overflow-auto"
                  title={`${entry.description}`}
                >
                  {entry.description || ""}
                </div>
                <div className="p-1 border-b text-center border-[#dee2e6] w-[10%]">
                  {formatAmountComma(entry.value) || ""}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                  {entry.startdate
                    ? formatDateByCountry(entry.startdate)
                    : ""}                   </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                  {entry.enddate
                    ? formatDateByCountry(entry.enddate)
                    : ""}                   </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center overflow-auto max-h-14"
                  title={`${entry.notes}`}
                >
                  {entry.notes || ""}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%]">
                  {formatAmountComma(entry.expenses) || ""}
                </div>
                <div className="p-2.5 border-b border-[#dee2e6] w-[40%] text-center">
                  <ProgressTracker value={entry.isTaskComplete ? 100 : 0} />
                </div>
              </div>
            ))
          ) : (
            <div className="flex text-[14px] break-all whitespace-normal max-h-14 p-1">
              <p className="text-red-500 text-center">
                {" "}
                No Tracking Task Entries Found
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ShowCompletedTasksOP
